import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, NotebookPen, Video, X, ExternalLink, Calendar, BookOpen, ClipboardList } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export function LearnerBookmarksPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/learner/bookmarks"); setRows(data.data.bookmarks); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/learner/bookmarks/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="My bookmarks" breadcrumbs={["My Learning", "Bookmarks"]}>
      <div className="space-y-4" data-testid="learner-bookmarks">
        {loading ? <LoadingBlock label="Loading bookmarks…" /> : rows.length === 0 ? (
          <EmptyState title="No bookmarks yet" description="Bookmark lessons from the course player to see them here." icon={Bookmark} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Lesson</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Course</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Note</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">When</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="text-sm font-semibold text-slate-900">{b.lesson?.lesson_title || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{b.course?.course_title || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{b.bookmark_note || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{b.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => remove(b.id)} className="text-xs font-semibold text-rose-600 hover:text-rose-800">Remove</button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export function LearnerLiveClassesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/learner/live-classes/upcoming"); setRows(data.data.live_classes); }
      catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout title="Upcoming live classes" breadcrumbs={["My Learning", "Live classes"]}>
      <div className="space-y-4" data-testid="learner-live-classes">
        {loading ? <LoadingBlock label="Loading…" /> : rows.length === 0 ? (
          <EmptyState title="No upcoming live classes" description="Your instructor will schedule live sessions here." icon={Video} />
        ) : rows.map((c) => (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#329112]">{c.course_title}</div>
              <div className="mt-1 text-lg font-bold text-slate-900">{c.class_title}</div>
              <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {c.class_date?.slice(0, 10)}</span>
                <span>·</span>
                <span>{c.start_time} – {c.end_time} ({c.timezone})</span>
                <span>·</span>
                <span className="capitalize">{c.meeting_platform}</span>
                <StatusBadge status={c.status} />
              </div>
              {c.description && <p className="mt-2 text-sm text-slate-600">{c.description}</p>}
            </div>
            <a href={c.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-bold text-white hover:bg-[#0c135c]" data-testid={`live-join-${c.id}`}>
              <ExternalLink className="h-4 w-4" /> Join
            </a>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export function OrgLearningProgressPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ course_id: "all", status: "all", department_id: "all", search: "" });

  // Load filter option lists (courses + departments) once. These endpoints are
  // idempotent GETs that already exist for the mapped-courses and staff pages.
  useEffect(() => {
    (async () => {
      try {
        const [cRes, dRes] = await Promise.all([
          api.get("/org/courses"),
          api.get("/org/departments"),
        ]);
        setCourses(cRes.data.data.mappings?.map((m) => m.course).filter(Boolean) || []);
        setDepartments(dRes.data.data.departments || []);
      } catch { /* silent; filters remain empty */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.course_id !== "all") params.course_id = filters.course_id;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.department_id !== "all") params.department_id = filters.department_id;
      if (filters.search.trim()) params.search = filters.search.trim();
      const { data: r } = await api.get("/org/learning-progress", { params });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  if (loading && !data) return <DashboardLayout title="Learning progress"><LoadingBlock label="Loading progress…" /></DashboardLayout>;
  if (!data) return null;

  const rows = data.assignments || [];

  return (
    <DashboardLayout title="Staff learning progress" breadcrumbs={["My Institute", "Learning progress"]}>
      <div className="space-y-6" data-testid="org-learning-progress">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label="Total assignments" value={data.totals.assignments} icon="ClipboardList" tone="navy" testId="lp-kpi-total" index={0} />
          <StatCard label="Not started" value={data.totals.not_started} icon="Circle" tone="slate" testId="lp-kpi-notstarted" index={1} />
          <StatCard label="In progress" value={data.totals.in_progress} icon="Loader" tone="amber" testId="lp-kpi-inprogress" index={2} />
          <StatCard label="Completed" value={data.totals.completed} icon="CircleCheckBig" tone="green" testId="lp-kpi-completed" index={3} />
          <StatCard label="Overdue" value={data.totals.overdue} icon="AlarmClock" tone="amber" testId="lp-kpi-overdue" index={4} />
        </section>

        {/* Filters */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="lp-filters">
          <Input
            value={filters.search}
            onChange={(e) => set("search")(e.target.value)}
            placeholder="Search learner name, email or course…"
            className="w-72"
            data-testid="lp-filter-search"
          />
          <Select value={filters.course_id} onValueChange={set("course_id")}>
            <SelectTrigger className="w-56" data-testid="lp-filter-course"><SelectValue placeholder="All courses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.department_id} onValueChange={set("department_id")}>
            <SelectTrigger className="w-48" data-testid="lp-filter-department"><SelectValue placeholder="All departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.department_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={set("status")}>
            <SelectTrigger className="w-40" data-testid="lp-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="not_started">Not started</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {(filters.course_id !== "all" || filters.status !== "all" || filters.department_id !== "all" || filters.search) && (
            <button type="button" className="text-xs font-semibold text-slate-500 hover:text-[#101876]" onClick={() => setFilters({ course_id: "all", status: "all", department_id: "all", search: "" })} data-testid="lp-filter-clear">
              Clear filters
            </button>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 flex items-center justify-between">
            <span>Assignments <span className="text-xs font-normal text-slate-500">· showing {rows.length}</span></span>
          </div>
          {rows.length === 0 ? (
            <EmptyState title="No assignments match" description="Try clearing filters or assigning courses first." icon={ClipboardList} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Learner</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Department</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Course</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Priority</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Progress</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Assigned</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((a) => (
                    <TableRow key={a.id} data-testid={`lp-row-${a.id}`}>
                      <TableCell>
                        <div className="text-sm font-semibold text-slate-800">{a.user_name}</div>
                        <div className="text-[11px] text-slate-500">{a.user_email}</div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{a.department_name}</TableCell>
                      <TableCell>
                        <div className="text-sm font-semibold text-slate-800">{a.course_title}</div>
                        {a.course_code && <div className="text-[11px] font-mono text-slate-500">{a.course_code}</div>}
                      </TableCell>
                      <TableCell>
                        {(a.mandatory_or_optional === "mandatory" || a.is_mandatory === true) ? (
                          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">Mandatory</span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">Optional</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-24 rounded-full bg-slate-100">
                            <div className="h-1.5 rounded-full bg-[#329112]" style={{ width: `${a.progress_percentage || 0}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 tabular-nums">{a.progress_percentage || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={a.status} /></TableCell>
                      <TableCell className="text-[11px] text-slate-500 whitespace-nowrap">{a.assigned_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
